import {
  Box,
  Button,
  ContentLayout,
  Header,
  KeyValuePairs,
  Link,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import { Timestamp } from '@firebase/firestore';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteItem, fetchItem, fetchItemsByIds, fetchItemsWithOrFilters, updateItem } from '../api/generic';
import { fetchUrlFromPath } from '../api/storage';
import GraphView from '../components/modules/GraphView';
import GridLinkModal from '../components/modules/GridLinkModal';
import { useAlertContext } from '../contexts/AlertContext';
import { useAuthContext } from '../contexts/AuthContext';
import { Batch } from '../models/Batch';
import { GraphLink } from '../models/GraphLink';
import InventoryItem from '../models/Inventory';
import { Invoice } from '../models/Invoice';
import { isValidFirestoreDate } from '../utils/date';
import { prefixCollection } from '../utils/prefix';

const InvoicePage = () => {
  const alert = useAlertContext();
  const { id } = useParams();
  const { tenant } = useAuthContext();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<Invoice>();
  const [url, setUrl] = useState<string>();
  const [showModal, setShowModal] = useState(false);

  const [graphData, setGraphData] = useState<GraphLink[]>([]);
  const [batchData, setBatchData] = useState<Batch[]>([]);
  const [inventoryData, setInventoryData] = useState<InventoryItem[]>([]);

  const fetchGraphData = async () => {
    const response = await fetchItemsWithOrFilters<GraphLink>(prefixCollection('graphLinks', tenant), {
      or: [
        { field: 'from', operator: '==', value: id },
        { field: 'to', operator: '==', value: id },
      ],
    });
    setGraphData(response);

    const batchIds = response
      .filter((x) => x.type.includes('batch'))
      .map((x) => {
        return x.from === 'batch' ? x.from : x.to;
      });

    const inventoryIds = response
      .filter((x) => x.type.includes('inventory'))
      .map((x) => {
        return x.type[0] === 'inventory' ? x.from : x.to;
      });

    const batches = await fetchItemsByIds<Batch>(prefixCollection('batches', tenant), batchIds);
    const inventory = await fetchItemsByIds<InventoryItem>(prefixCollection('inventory', tenant), inventoryIds);

    setBatchData(batches);
    setInventoryData(inventory);
  };

  const fetchInvoice = async () => {
    if (!id) return;
    const response = await fetchItem<Invoice>(prefixCollection('invoices', tenant), id);

    if (response) {
      setUrl(await fetchUrlFromPath(response.fileUrl));
      setInvoice(response);
    }
  };

  useEffect(() => {
    fetchInvoice();
    fetchGraphData();
  }, [id]);

  const fixDateAndSave = async () => {
    if (!invoice || !id) return;

    const rawDate = new Date(invoice.invoiceDate.toString());
    if (isNaN(rawDate.getTime())) {
      alert.error('Invalid date format');
      return;
    }

    const fixedInvoice = {
      ...invoice,
      invoiceDate: Timestamp.fromDate(rawDate), // ✅ proper Firestore date
    };

    await updateItem<Invoice>(prefixCollection('invoices', tenant), id, fixedInvoice);
    fetchInvoice(); // Refresh the invoice data after saving
  };

  return (
    <>
      <ContentLayout
        disableOverlap
        header={
          <Header
            variant="h1"
            description={
              'Hint: All ingredients in a batch must be linked to an invoice. For audit tracking, you can link multiple invoices to a batch.'
            }
            actions={
              <SpaceBetween size="m" direction="horizontal">
                <Button variant="normal" iconName={'angle-left'} onClick={() => navigate(-1)}>
                  Back
                </Button>
                <Button
                  variant="normal"
                  iconName={'delete-marker'}
                  onClick={async () => {
                    if (!id) return;

                    const confirmed = window.confirm('Are you sure you want to delete this invoice?');
                    if (!confirmed) return;

                    await deleteItem(prefixCollection('invoices', tenant), id);
                    navigate('/invoices');
                  }}
                >
                  Delete
                </Button>

                <Button variant="normal" iconName={'grid-view'} onClick={() => setShowModal(true)}>
                  Link invoice
                </Button>
                {invoice && !isValidFirestoreDate(invoice?.invoiceDate) && (
                  <Button
                    variant="normal"
                    iconName={'status-warning'}
                    onClick={() => {
                      fixDateAndSave();
                    }}
                  >
                    ⚠️Fix date!
                  </Button>
                )}
              </SpaceBetween>
            }
          >
            {invoice?.supplierName} - {invoice?.invoiceNumber ?? ''}
          </Header>
        }
      >
        {invoice ? (
          <SpaceBetween size="l" direction="vertical">
            <KeyValuePairs
              columns={3}
              items={[
                { label: 'Supplier', value: invoice.supplierName },
                { label: 'Invoice number', value: invoice.invoiceNumber },
                {
                  label: 'Invoice date',
                  value: new Date(invoice.invoiceDate.toString()).toLocaleDateString(),
                },
                {
                  label: 'Total Amount Excl. VAT',
                  value: `${invoice.totalAmountExclVat} ${invoice.currency}`,
                },
                {
                  label: 'VAT Amount',
                  value: `${invoice.vatAmount} ${invoice.currency}`,
                },
                {
                  label: 'Total Amount Incl. VAT',
                  value: <b>{`${invoice.totalAmountInclVat} ${invoice.currency}`}</b>,
                },
                { label: 'Notes', value: invoice.notes },
                {
                  label: 'File',
                  value: (
                    <Link external={true} href={url}>
                      {invoice.fileUrl}
                    </Link>
                  ),
                },
              ]}
            />

            {invoice?.items && (
              <SpaceBetween size="m" direction="vertical">
                <Box variant="h3">Items</Box>
                <KeyValuePairs
                  columns={4}
                  items={invoice.items.flatMap((item, i) => [
                    {
                      label: i === 0 ? 'Description' : '',
                      value: item.description,
                    },
                    {
                      label: i === 0 ? 'Per Unit' : '',
                      value: `${item.unitPrice} ${invoice.currency}`,
                    },
                    {
                      label: i === 0 ? 'Quantity' : '',
                      value: `${item.quantity} ${item.unit}`,
                    },
                    {
                      label: i === 0 ? 'Total' : '',
                      value: `${item.total} ${invoice.currency}`,
                    },
                  ])}
                />
              </SpaceBetween>
            )}

            {id && graphData?.length > 1 && (
              <GraphView
                id={id}
                type={'invoice'}
                types={['batch', 'inventory']}
                positions={['right', 'down']}
                graphData={graphData}
                labels={[
                  ...batchData.map((batch) => {
                    return {
                      label: `${batch.batchId} ${batch.name}`,
                      value: batch.id,
                      type: 'batch',
                    };
                  }),
                  ...inventoryData.map((item) => {
                    return {
                      label: item.name,
                      value: item.id,
                      type: 'inventory',
                    };
                  }),
                  {
                    label: `${invoice?.supplierName} - ${invoice?.invoiceNumber ?? ''}`,
                    value: id,
                    type: 'invoice',
                  },
                ]}
              />
            )}
          </SpaceBetween>
        ) : (
          <Spinner size="big" />
        )}
      </ContentLayout>

      {id && (
        <GridLinkModal
          showModal={showModal}
          setShowModal={setShowModal}
          fromId={id}
          fromType="invoice"
          toTypes={['batch', 'inventory']}
          onChange={async () => {
            await fetchGraphData();
          }}
        />
      )}
    </>
  );
};

export default InvoicePage;
